import { useMemo, useState } from "react";
import "./recipe-grade-multi-select.scss";

type GradeRow = { id: number; name?: string | null };

type Props = {
  disabled?: boolean;
  grades: GradeRow[];
  value: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  /** Show error border (e.g. after submit validation). */
  invalid?: boolean;
};

export default function RecipeGradeMultiSelect({
  disabled,
  grades,
  value,
  onChange,
  placeholder = "Search and select grades…",
  invalid,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return grades;
    return grades.filter((g) => (g.name ?? "").toLowerCase().includes(s));
  }, [grades, q]);

  const byId = useMemo(() => new Map(grades.map((g) => [g.id, g])), [grades]);

  function toggle(id: number) {
    if (disabled) return;
    const set = new Set(value);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set).sort((a, b) => a - b));
  }

  function remove(id: number) {
    if (disabled) return;
    onChange(value.filter((x) => x !== id));
  }

  return (
    <div
      className={`rg-ms${disabled ? " rg-ms--disabled" : ""}${invalid ? " rg-ms--invalid" : ""}`}
    >
      <div className="rg-ms__control" onClick={() => !disabled && setOpen((o) => !o)}>
        <div className="rg-ms__tags">
          {value.length === 0 ? (
            <span className="rg-ms__placeholder">{placeholder}</span>
          ) : (
            value.map((id) => (
              <button
                key={id}
                type="button"
                className="rg-ms__tag"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(id);
                }}
              >
                {(byId.get(id)?.name ?? "").trim() || `Grade #${id}`}
                <span className="rg-ms__tag-x" aria-hidden>
                  ×
                </span>
              </button>
            ))
          )}
        </div>
        <span className="rg-ms__caret" aria-hidden>
          ▾
        </span>
      </div>
      {open && !disabled && (
        <div className="rg-ms__panel">
          <input
            type="search"
            className="rg-ms__search"
            placeholder="Search grades…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
          <ul className="rg-ms__list" role="listbox" aria-multiselectable>
            {filtered.map((g) => {
              const checked = value.includes(g.id);
              return (
                <li key={g.id}>
                  <label className="rg-ms__row">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(g.id)}
                    />
                    <span>{(g.name ?? "").trim() || `Grade #${g.id}`}</span>
                  </label>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="rg-ms__empty">No grades match your search.</li>
            )}
          </ul>
        </div>
      )}
      {open && !disabled && (
        <button
          type="button"
          className="rg-ms__backdrop"
          aria-label="Close grade picker"
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
}
